/**
 * resetAllStores — wipe all in-memory, user-scoped store state + module caches on logout / account switch.
 *
 * The P0 cross-user leak fix: Zustand stores and module-level caches live in memory and survive an in-app
 * logout (which is a client-side navigate, NOT a page reload). Without this, the next user (same tab) would
 * see the prior user's catalog/drafts/albums/artists/player/homepage rows plus their signed master URLs
 * until each store/cache happened to refetch or expire (60s–10min).
 *
 * Called from AuthProvider.onAuthStateChange on SIGNED_OUT and on a user-id change, BEFORE the new identity
 * is committed (see providers/AuthProvider.tsx).
 *
 * NOTE: store reset()s clear DATA only. Component-owned subscriptions (e.g. the drafts realtime channel,
 * owned by DraftsPage via useDraftsStore.subscribeToChanges) are torn down on their component's unmount —
 * do NOT tear them down here, or the component's unsubscribe bookkeeping desyncs.
 */
import { clearSignedUrlCache } from "@/lib/backend-signed-urls";
import { useAlbumsStore } from "@/lib/albums-store";
import { useArtistsStore } from "@/lib/artists-store";
import { useCatalogStore } from "@/lib/catalog";
import { useDraftsStore } from "@/lib/drafts";
import { useHomepageStore } from "@/lib/homepage-store";
import { useLicenseCampaignsStore } from "@/lib/license-campaigns";
import { useLicenseProjectsStore } from "@/lib/license-projects";
import { useLicenseRequestsStore } from "@/lib/license-requests";
import { useNotificationsStore } from "@/lib/notifications";
import { usePlayerStore } from "@/lib/player";
import { usePlaylistsStore } from "@/lib/playlists";
import { useStorefrontStore } from "@/lib/storefront";
import { clearTracksCache, useTracksStore } from "@/lib/tracks";

export function resetAllStores(): void {
  usePlayerStore.getState().reset();
  useCatalogStore.getState().reset();
  useDraftsStore.getState().reset();
  useTracksStore.getState().reset();
  useAlbumsStore.getState().reset();
  useArtistsStore.getState().reset();
  useHomepageStore.getState().reset();
  // The storefront store's reset() also empties its request cache, whose reads were made for the
  // previous viewer (the backend shapes the track rows by who asks).
  useStorefrontStore.getState().reset();
  // Playlists: the account's own lists, the open one and the selection. The playlist page
  // (/playlist/:id) loads the list only when it is empty and otherwise trusts it, so without this
  // the next account could open the previous account's playlist from memory, as its owner.
  usePlaylistsStore.getState().reset();
  // Licensing: the request list + its status filter + tab counts + the open request's
  // viewer-shaped detail, the buyer's projects (list, filters, open project) and campaign folders.
  // Each had a reset() that nothing called, so the next account saw the previous account's rows
  // and filter on /licenses, /projects and the home page until its own first fetch landed.
  useLicenseRequestsStore.getState().reset();
  useLicenseProjectsStore.getState().reset();
  useLicenseCampaignsStore.getState().reset();
  // The bell: its notices name requests and tracks of the account that received them, and it only
  // refetches the list while `loadedOnce` is false — so without this it showed them to the next
  // account until a page reload.
  useNotificationsStore.getState().reset();

  // Module-level caches that are NOT user-keyed (so a new user could otherwise read the prior user's data).
  clearTracksCache();
  clearSignedUrlCache();
}
